import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useUserAuth } from "../../context/userAuthContext";
import { doc, getDoc, getFirestore } from "firebase/firestore";
import {
  Settings,
  QrCode,
  Languages,
  Moon,
  Shield,
  HelpCircle,
  ChevronRight,
  LogOut,
  UserCircle,
  Sparkles,
  Users,
  Edit,
  X,
  Download,
  Share,
  Copy,
  Check,
  Bell,
  Search,
  Star,
  TrendingUp,
  MessageCircle,
  Heart,
  Award,
  Zap,
  CreditCard,
  DollarSign,
  PlusCircle,
  MinusCircle,
  FileText,
  Calculator,
  Briefcase,
  PiggyBank,
  ArrowUpCircle,
  ArrowDownCircle,
  ArrowLeft,
  Gift,
} from "lucide-react";

const MiguelMXSettings = () => {
  const [userInfo, setUserInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [darkMode, setDarkMode] = useState(false);
  const [notificationsEnabled, setNotificationsEnabled] = useState(true);
  const [showQR, setShowQR] = useState(false);
  const [copied, setCopied] = useState(false);
  const navigate = useNavigate();
  const { user, logOut } = useUserAuth();

  useEffect(() => {
    const fetchUserData = async () => {
      try {
        if (user?.uid) {
          const firestore = getFirestore();
          const docRef = doc(firestore, "Users", user.uid);
          const docSnap = await getDoc(docRef);

          if (docSnap.exists()) {
            const userData = docSnap.data();
            setUserInfo({
              name: `${userData.nombre || ""} ${
                userData.apellido || ""
              }`.trim(),
              nickname: userData.nickname || "",
              photoUrl: userData.photoUrl || "",
              uid: user.uid,
              email: userData.email || user.email,
            });
          } else {
            setUserInfo({
              name: user.displayName || "Usuario",
              nickname: "",
              photoUrl: user.photoURL || "",
              uid: user.uid,
              email: user.email,
            });
          }
        }
      } catch (error) {
        console.error("Error fetching user data:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchUserData();
  }, [user]);

  const handleLogout = async () => {
    try {
      await logOut();
      navigate("/login");
    } catch (error) {
      console.error("Error logging out:", error);
    }
  };

  const copyToClipboard = async (text) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error("Error copying to clipboard:", err);
    }
  };

  const generateQRCode = (data) => {
    const qrData = encodeURIComponent(
      JSON.stringify({
        uid: data.uid,
        name: data.name,
        nickname: data.nickname,
        app: "MiguelMX",
      })
    );
    return `https://api.qrserver.com/v1/create-qr-code/?size=300x300&data=${qrData}&bgcolor=fff&color=14840c&ecc=M&margin=10`;
  };

  const transactionItems = [
    {
      icon: <ArrowUpCircle className="w-6 h-6 text-[#14840c]" />,
      label: "Agregar Ingreso",
      description: "Registra dinero que entra",
      path: "/incomes",
      stats: "Último: $15,000",
      highlight: true,
    },
    {
      icon: <ArrowDownCircle className="w-6 h-6 text-red-500" />,
      label: "Agregar Gasto",
      description: "Registra dinero que sale",
      path: "/expenses",
      stats: "Último: $2,500",
    },
    {
      icon: <CreditCard className="w-6 h-6 text-blue-500" />,
      label: "Suscripciones",
      description: "Servicios recurrentes",
      path: "/subscriptions",
      stats: "8 activas",
    },
    {
      icon: <TrendingUp className="w-6 h-6 text-purple-500" />,
      label: "Inversiones",
      description: "Portfolio y análisis",
      path: "/investments",
      stats: "+12.5%",
    },
  ];

  const servicesItems = [
    {
      icon: <Shield className="w-6 h-6 text-blue-600" />,
      label: "Mis Pólizas",
      description: "Seguros contratados",
      path: "/polizas",
      stats: "3 activas",
    },
    {
      icon: <Gift className="w-6 h-6 text-green-600" />,
      label: "Cashback",
      description: "Recompensas disponibles",
      path: "/cashback",
      stats: "$1,250 MXN",
      highlight: true,
    },
    {
      icon: <MessageCircle className="w-6 h-6 text-indigo-500" />,
      label: "Mis Consultas",
      description: "Asesorías programadas",
      path: "/consultas",
      stats: "2 pendientes",
    },
    {
      icon: <FileText className="w-6 h-6 text-orange-500" />,
      label: "Reportes",
      description: "Análisis financiero",
      path: "/reportes",
      stats: "Mensual",
    },
  ];

  const settingsItems = [
    {
      icon: <QrCode className="w-6 h-6 text-[#14840c]" />,
      label: "Mi Código QR",
      description: "Compartir perfil",
      action: () => setShowQR(true),
    },
    {
      icon: <Bell className="w-6 h-6 text-yellow-500" />,
      label: "Notificaciones",
      description: "Alertas y recordatorios",
      toggle: true,
      enabled: notificationsEnabled,
      onToggle: () => setNotificationsEnabled(!notificationsEnabled),
    },
    {
      icon: <Moon className="w-6 h-6 text-purple-500" />,
      label: "Tema Oscuro",
      description: "Modo nocturno",
      toggle: true,
      enabled: darkMode,
      onToggle: () => setDarkMode(!darkMode),
    },
    {
      icon: <Languages className="w-6 h-6 text-blue-500" />,
      label: "Idioma",
      description: "Español (México)",
      value: "🇲🇽",
    },
    {
      icon: <Shield className="w-6 h-6 text-red-500" />,
      label: "Seguridad",
      description: "PIN y privacidad",
      path: "/security",
    },
    {
      icon: <HelpCircle className="w-6 h-6 text-gray-500" />,
      label: "Ayuda",
      description: "Soporte y FAQ",
      path: "/support",
    },
  ];

  const MenuItem = ({ item, className = "" }) => (
    <div
      className={`group flex items-center justify-between bg-white/80 backdrop-blur-sm p-4 lg:p-5 rounded-2xl shadow-md border border-green-100 cursor-pointer hover:bg-white hover:shadow-lg hover:scale-[1.02] transition-all duration-300 active:scale-[0.98] hover:border-[#14840c]/20 ${className}`}
      onClick={() => {
        if (item.path) {
          navigate(item.path);
        } else if (item.action) {
          item.action();
        } else if (item.toggle && item.onToggle) {
          item.onToggle();
        }
      }}
    >
      <div className="flex items-center gap-4">
        <div className="p-3 bg-white rounded-xl shadow-sm group-hover:shadow-md transition-all duration-300 border border-gray-100">
          {item.icon}
        </div>
        <div className="flex-1">
          <div className="font-bold text-slate-800 text-base">{item.label}</div>
          <div className="text-sm text-slate-600">{item.description}</div>
          {item.stats && (
            <div
              className={`text-xs mt-1 px-2 py-1 rounded-full inline-block font-medium ${
                item.highlight
                  ? "bg-gradient-to-r from-[#14840c] to-green-600 text-white"
                  : "bg-slate-100 text-slate-600"
              }`}
            >
              {item.stats}
            </div>
          )}
        </div>
      </div>
      <div className="flex items-center gap-3">
        {item.value && (
          <span className="text-slate-600 text-lg">{item.value}</span>
        )}
        {item.toggle ? (
          <div
            className={`w-12 h-6 rounded-full transition-all duration-300 ${
              item.enabled
                ? "bg-gradient-to-r from-[#14840c] to-green-600"
                : "bg-slate-300"
            }`}
          >
            <div
              className={`w-5 h-5 bg-white rounded-full transform transition-all duration-300 shadow-lg ${
                item.enabled ? "translate-x-6" : "translate-x-0.5"
              } mt-0.5`}
            />
          </div>
        ) : (
          <ChevronRight className="w-5 h-5 text-slate-400 group-hover:text-[#14840c] transition-colors" />
        )}
      </div>
    </div>
  );

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-green-50 to-emerald-50">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-[#14840c] border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-slate-600 font-medium">Cargando tu perfil...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-green-50 via-emerald-50 to-slate-50">
      {/* Header */}
      <div className="sticky top-0 bg-white/90 backdrop-blur-lg z-10 px-4 py-4 border-b border-green-100 shadow-sm">
        <div className="flex items-center justify-between max-w-6xl mx-auto">
          <div className="flex items-center gap-3">
            <button
              onClick={() => navigate(-1)}
              className="p-2 hover:bg-green-100 rounded-xl transition-colors"
            >
              <ArrowLeft className="w-6 h-6 text-slate-600" />
            </button>
            <div>
              <h1 className="text-xl lg:text-2xl font-black text-slate-900">
                Mi Perfil
              </h1>
              <p className="text-sm text-slate-600 hidden lg:block">
                Gestiona tu cuenta MiguelMX
              </p>
            </div>
          </div>

          <div className="flex items-center gap-2">
            <button className="p-2 hover:bg-green-100 rounded-xl transition-colors">
              <Bell className="w-5 h-5 text-slate-600" />
            </button>
            <button
              onClick={handleLogout}
              className="p-2 text-red-500 hover:bg-red-50 rounded-xl transition-all duration-200"
            >
              <LogOut className="w-5 h-5" />
            </button>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="p-4 lg:p-8">
        <div className="max-w-6xl mx-auto">
          {/* Profile Section */}
          <div className="grid grid-cols-1 lg:grid-cols-4 gap-6 mb-8">
            {/* Profile Card */}
            <div className="lg:col-span-1">
              <div className="bg-gradient-to-br from-white to-green-50 p-6 rounded-3xl shadow-xl border border-green-100 sticky top-24">
                <div className="text-center">
                  <div className="relative mx-auto mb-6">
                    <div className="w-24 h-24 lg:w-28 lg:h-28 rounded-full overflow-hidden border-4 border-white shadow-2xl mx-auto ring-4 ring-[#14840c]/10">
                      {userInfo?.photoUrl ? (
                        <img
                          src={userInfo.photoUrl}
                          alt="Profile"
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        <div className="w-full h-full bg-gradient-to-br from-green-100 to-emerald-100 flex items-center justify-center">
                          <UserCircle className="w-16 h-16 lg:w-20 lg:h-20 text-[#14840c]" />
                        </div>
                      )}
                    </div>
                    <div className="absolute -bottom-2 -right-2 bg-gradient-to-r from-[#14840c] to-green-600 rounded-full p-2 shadow-lg">
                      <Sparkles className="w-4 h-4 text-white" />
                    </div>
                  </div>

                  <h2 className="text-xl lg:text-2xl font-black text-slate-900 mb-2">
                    {userInfo?.name || "Usuario"}
                  </h2>
                  {userInfo?.nickname && (
                    <p className="text-[#14840c] font-bold text-lg mb-1">
                      @{userInfo.nickname}
                    </p>
                  )}
                  <p className="text-slate-500 text-sm mb-4">
                    {userInfo?.email}
                  </p>

                  {/* Stats */}
                  <div className="grid grid-cols-2 gap-4 mb-6">
                    <div className="text-center bg-white/60 rounded-xl p-3">
                      <div className="text-2xl font-black text-slate-800">
                        12
                      </div>
                      <div className="text-xs text-slate-500 font-medium">
                        Servicios
                      </div>
                    </div>
                    <div className="text-center bg-white/60 rounded-xl p-3">
                      <div className="text-2xl font-black text-[#14840c]">
                        Pro
                      </div>
                      <div className="text-xs text-slate-500 font-medium">
                        Membresía
                      </div>
                    </div>
                  </div>

                  {/* Action Buttons */}
                  <div className="space-y-3">
                    <button
                      onClick={() => navigate("/addprofile")}
                      className="w-full bg-gradient-to-r from-[#14840c] to-green-600 text-white py-3 rounded-2xl font-bold hover:shadow-lg transition-all duration-300 transform hover:scale-[1.02] active:scale-[0.98] flex items-center justify-center gap-2"
                    >
                      <Edit className="w-4 h-4" />
                      Editar Perfil
                    </button>

                    <button
                      onClick={() => setShowQR(true)}
                      className="w-full bg-white border-2 border-[#14840c] text-[#14840c] py-3 rounded-2xl font-bold hover:bg-green-50 transition-all duration-300 transform hover:scale-[1.02] active:scale-[0.98] flex items-center justify-center gap-2"
                    >
                      <QrCode className="w-4 h-4" />
                      Mi Código QR
                    </button>
                  </div>
                </div>
              </div>
            </div>

            {/* Menu Sections */}
            <div className="lg:col-span-3 space-y-8">
              {/* Transactions Section */}
              <div>
                <h3 className="text-2xl font-black text-slate-900 mb-6 px-2 flex items-center gap-3">
                  <DollarSign className="w-7 h-7 text-[#14840c]" />
                  Finanzas
                </h3>
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                  {transactionItems.map((item, index) => (
                    <MenuItem key={`transaction-${index}`} item={item} />
                  ))}
                </div>
              </div>

              {/* Services Section */}
              <div>
                <h3 className="text-2xl font-black text-slate-900 mb-6 px-2 flex items-center gap-3">
                  <Briefcase className="w-7 h-7 text-blue-600" />
                  Servicios
                </h3>
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                  {servicesItems.map((item, index) => (
                    <MenuItem key={`service-${index}`} item={item} />
                  ))}
                </div>
              </div>

              {/* Settings Section */}
              <div>
                <h3 className="text-2xl font-black text-slate-900 mb-6 px-2 flex items-center gap-3">
                  <Settings className="w-7 h-7 text-purple-600" />
                  Configuración
                </h3>
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                  {settingsItems.map((item, index) => (
                    <MenuItem key={`settings-${index}`} item={item} />
                  ))}
                </div>
              </div>
            </div>
          </div>

          {/* Footer */}
          <div className="text-center py-8">
            <div className="flex items-center justify-center gap-2 mb-4">
              <img
                src="https://firebasestorage.googleapis.com/v0/b/xfinance-12e10.firebasestorage.app/o/Logos%2Flogo192.png?alt=media&token=0dd60c18-dff8-4b94-ad27-b31256efd17f"
                alt="MiguelMX"
                className="h-6 w-6"
              />
              <span className="font-black text-[#14840c]">MiguelMX®</span>
            </div>
            <p className="text-sm text-slate-500">
              v2.0 • Fintech 100% Mexicana • Hecho con ❤️ en Sonora
            </p>
          </div>
        </div>
      </div>

      {/* QR Code Modal */}
      {showQR && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-3xl shadow-2xl max-w-md w-full overflow-hidden">
            {/* Header */}
            <div className="bg-gradient-to-r from-[#14840c] to-green-600 p-6 text-white">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-xl font-black">Mi Código QR</h3>
                <button
                  onClick={() => setShowQR(false)}
                  className="p-2 hover:bg-white/20 rounded-full transition-colors"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
              <p className="text-green-100">Comparte tu perfil de MiguelMX</p>
            </div>

            {/* QR Code */}
            <div className="p-8 text-center">
              <div className="bg-white p-6 rounded-2xl shadow-lg inline-block mb-6 border border-green-100">
                <img
                  src={generateQRCode(userInfo)}
                  alt="QR Code"
                  className="w-48 h-48 rounded-xl"
                />
              </div>

              <div className="mb-6">
                <h4 className="font-black text-slate-900 text-lg mb-1">
                  {userInfo?.name}
                </h4>
                {userInfo?.nickname && (
                  <p className="text-[#14840c] font-bold text-lg">
                    @{userInfo.nickname}
                  </p>
                )}
              </div>

              {/* Actions */}
              <div className="grid grid-cols-2 gap-4">
                <button
                  onClick={() => copyToClipboard(userInfo?.uid)}
                  className="flex items-center justify-center gap-2 p-4 bg-slate-100 rounded-xl hover:bg-slate-200 transition-colors"
                >
                  {copied ? (
                    <>
                      <Check className="w-4 h-4 text-green-500" />
                      <span className="text-sm font-bold text-green-600">
                        Copiado
                      </span>
                    </>
                  ) : (
                    <>
                      <Copy className="w-4 h-4 text-slate-600" />
                      <span className="text-sm font-bold text-slate-700">
                        Copiar ID
                      </span>
                    </>
                  )}
                </button>

                <button className="flex items-center justify-center gap-2 p-4 bg-gradient-to-r from-[#14840c] to-green-600 text-white rounded-xl hover:shadow-lg transition-all">
                  <Share className="w-4 h-4" />
                  <span className="text-sm font-bold">Compartir</span>
                </button>
              </div>
            </div>

            {/* Footer */}
            <div className="bg-slate-50 p-4 text-center">
              <p className="text-xs text-slate-500">
                ID: {userInfo?.uid?.slice(0, 8)}...
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MiguelMXSettings;
